import { ReactNode } from "react";
import moment from "moment";

/*
 * Device-health rendering helpers for the Devices table.
 *
 * Telemetry reported by the firmware every upload cycle:
 *   battery      - pack voltage (Number)
 *   status       - signal: GSM CSQ 0..31 (higher = better, 99 = unknown) or WiFi RSSI dBm
 *   rtcInternal  - true = external RTC chip failed, device fell back to the internal RTC
 *                  (this path does an extra re-login every cycle and drains faster)
 *   lastSeen     - last time the device contacted the server
 *   bootCount    - lifetime wake count; jumps/resets can indicate brownout loops
 */

// --- Tuning constants -------------------------------------------------------
// NOTE: battery thresholds assume a single Li-ion cell. Calibrate to your actual
// pack/divider — the raw voltage is always shown, so colour is a hint, not gospel.
const BATTERY_GOOD_V = 3.7;
const BATTERY_WARN_V = 3.5;

// GSM signal quality (CSQ) thresholds
const SIGNAL_GOOD_CSQ = 15;
const SIGNAL_FAIR_CSQ = 8;

// A device is considered offline if it hasn't checked in within this window.
// Uploads happen every 6h (WAKEUPTIME), so allow ~2 cycles + margin.
const OFFLINE_AFTER_HOURS = 13;

const Badge = ({ tone, children }: { tone: string; children: ReactNode }) => (
  <span
    className={`badge badge-light-${tone}`}
    style={{
      color: "#000000",
      fontSize: "inherit",
      fontWeight: "inherit",
      fontFamily: "inherit",
    }}
  >
    {children}
  </span>
);

// `hasReported` should be `!!row.lastSeen` — the schema defaults battery/status/rtcInternal
// to falsy values (0/undefined/false), which are also legitimate readings (a dead battery
// IS 0V and should show red, not "—"). lastSeen is only ever set by the telemetry endpoints,
// so its presence is the one reliable signal for "this device has reported at least once."
export const renderBattery = (value: number | undefined, hasReported: boolean) => {
  if (!hasReported || value === undefined || value === null) return <Badge tone="secondary">—</Badge>;
  const tone = value >= BATTERY_GOOD_V ? "success" : value >= BATTERY_WARN_V ? "warning" : "danger";
  return <Badge tone={tone}>{`${Number(value).toFixed(2)} V`}</Badge>;
};

export const renderSignal = (value: number | undefined, hasReported: boolean) => {
  if (!hasReported || value === undefined || value === null || value === 99) {
    return <Badge tone="secondary">—</Badge>;
  }
  // WiFi RSSI comes through negative (dBm); GSM CSQ is 0..31.
  if (value < 0) {
    const tone = value >= -70 ? "success" : value >= -85 ? "warning" : "danger";
    return <Badge tone={tone}>{`${value} dBm`}</Badge>;
  }
  const tone = value >= SIGNAL_GOOD_CSQ ? "success" : value >= SIGNAL_FAIR_CSQ ? "warning" : "danger";
  return <Badge tone={tone}>{`CSQ ${value}`}</Badge>;
};

export const renderRtc = (rtcInternal: boolean | undefined, hasReported: boolean) => {
  if (!hasReported || rtcInternal === undefined || rtcInternal === null) {
    return <Badge tone="secondary">—</Badge>;
  }
  return rtcInternal ? (
    <Badge tone="warning">Internal (fallback)</Badge>
  ) : (
    <Badge tone="success">External OK</Badge>
  );
};

export const renderLastSeen = (lastSeen: string | undefined, updatedAt: string | undefined) => {
  const stamp = lastSeen || updatedAt;
  if (!stamp) return <Badge tone="secondary">Never</Badge>;
  const hoursAgo = moment().diff(moment(stamp), "hours", true);
  const online = hoursAgo <= OFFLINE_AFTER_HOURS;
  return (
    <span className="d-flex align-items-center">
      <span
        className={`bullet bullet-dot me-2 bg-${online ? "success" : "danger"}`}
        style={{ width: 8, height: 8, borderRadius: "50%" }}
      />
      <span title={moment(stamp).format("YYYY-MM-DD HH:mm")}>{moment(stamp).fromNow()}</span>
    </span>
  );
};
